// SharedRecipes.js
import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { axiosInstance } from '../axiosInstance';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
  Link,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Helmet } from 'react-helmet';

const stripHtmlTags = (text) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = text;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const SharedRecipes = () => {
  const { shareId } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const response = await axiosInstance.get(`/api/shared/shared-recipes/${shareId}`);
        setRecipe(response.data.recipe);
      } catch (err) {
        console.error('Error loading shared recipe:', err);
        setError('This recipe could not be found or may have expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [shareId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" mt={5}>
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60 }} />
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Button
          component={RouterLink}
          to="/"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          Back to Meal Planner
        </Button>
      </Box>
    );
  }

  const { recipeName, introduction, ingredients, instructions, tips, friendly_closing, imageUrl } = recipe;

  return (
    <Box maxWidth="md" mx="auto" mb={15} mt={4} px={2}>
      <Helmet>
        <title>{stripHtmlTags(recipeName)}</title>
        <meta name="description" content={stripHtmlTags(introduction)} />
        <meta property="og:title" content={stripHtmlTags(recipeName)} />
        <meta property="og:description" content={stripHtmlTags(introduction)} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <Paper elevation={3} sx={{ padding: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" fontWeight="bold">
            {stripHtmlTags(recipeName)}
          </Typography>
          {imageUrl && (
            <img
              src={imageUrl}
              alt={recipeName}
              style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
            />
          )}
        </Box>

        <Typography paragraph>{stripHtmlTags(introduction)}</Typography>

        <Typography variant="h6">Ingredients</Typography>
        <ul>
          {ingredients.map((item, i) => (
            <li key={i}>
              <Typography>{stripHtmlTags(item)}</Typography>
            </li>
          ))}
        </ul>

        <Typography variant="h6">Instructions</Typography>
        <ol>
          {instructions.map((step, i) => (
            <li key={i}>
              <Typography>{stripHtmlTags(step)}</Typography>
            </li>
          ))}
        </ol>

        {tips && tips.length > 0 && (
          <>
            <Typography variant="h6">Tips</Typography>
            <ul>
              {tips.map((tip, i) => (
                <li key={i}>
                  <Typography>{stripHtmlTags(tip)}</Typography>
                </li>
              ))}
            </ul>
          </>
        )}

        <Typography paragraph>{stripHtmlTags(friendly_closing)}</Typography>
      </Paper>

      <Box textAlign="center" mt={4}>
        <Link component={RouterLink} to="/">
          <Button variant="outlined" startIcon={<ArrowBackIcon />}>
            Back to Meal Planner
          </Button>
        </Link>
      </Box>
    </Box>
  );
};

export default SharedRecipes;
